'use client'

/**
 * Carte du monde SVG avec marqueur sur la ville d'origine.
 * Affiche Paris et la ville d'origine, reliés par une ligne en pointillés dorés.
 *
 * Coordonnées (lat/lng) → SVG (x/y) :
 *   x = (lng + 180) / 360 * 800  (largeur SVG = 800)
 *   y = (90 - lat) / 180 * 400   (hauteur SVG = 400)
 */

type ProvenanceMapProps = {
  lat: number
  lng: number
  city: string
  country: string
  rayonName: string
  origin: string
}

// Paris : 48.8566 N, 2.3522 E
const PARIS = { lat: 48.8566, lng: 2.3522 }

function toXY(lat: number, lng: number) {
  return {
    x: ((lng + 180) / 360) * 800,
    y: ((90 - lat) / 180) * 400,
  }
}

export function ProvenanceMap({
  lat,
  lng,
  city,
  country,
  rayonName,
  origin,
}: ProvenanceMapProps) {
  const originXY = toXY(lat, lng)
  const parisXY = toXY(PARIS.lat, PARIS.lng)

  return (
    <div className="relative rounded-xl overflow-hidden border border-gold/30 bg-gradient-to-br from-cuir/5 via-gold/5 to-emerald/5">
      <svg
        viewBox="0 0 800 400"
        className="w-full h-auto"
        aria-label={`Carte du monde : ${city} jusqu'à Paris`}
      >
        {/* Définitions — dégradés et motifs */}
        <defs>
          <linearGradient id="oceanGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fef3c7" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#fef3c7" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="landGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d97706" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#92400e" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="routeGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#d97706" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0.3" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Océan — fond doux */}
        <rect width="800" height="400" fill="url(#oceanGradient)" />

        {/* Continents stylisés — silhouettes approximatives */}
        {/* Amérique du Nord */}
        <path
          d="M 80 80 Q 100 60 130 80 L 180 100 Q 200 130 220 160 L 200 200 Q 170 220 140 200 L 100 170 Q 80 130 80 80 Z"
          fill="url(#landGradient)"
          stroke="#92400e"
          strokeWidth="0.5"
          opacity="0.7"
        />
        {/* Amérique du Sud */}
        <path
          d="M 200 220 Q 220 240 230 270 L 220 320 Q 210 350 200 340 L 190 310 Q 180 270 200 220 Z"
          fill="url(#landGradient)"
          stroke="#92400e"
          strokeWidth="0.5"
          opacity="0.7"
        />
        {/* Europe */}
        <path
          d="M 380 90 Q 410 80 440 100 L 460 130 Q 440 150 420 140 L 400 130 Q 380 110 380 90 Z"
          fill="url(#landGradient)"
          stroke="#92400e"
          strokeWidth="0.5"
          opacity="0.8"
        />
        {/* Afrique */}
        <path
          d="M 420 160 Q 460 150 480 180 L 490 240 Q 480 290 460 310 L 430 300 Q 410 260 415 220 Q 420 180 420 160 Z"
          fill="url(#landGradient)"
          stroke="#92400e"
          strokeWidth="0.5"
          opacity="0.8"
        />
        {/* Asie */}
        <path
          d="M 480 80 Q 550 70 650 100 L 700 140 Q 680 180 640 170 L 580 160 Q 530 140 500 120 Q 480 100 480 80 Z"
          fill="url(#landGradient)"
          stroke="#92400e"
          strokeWidth="0.5"
          opacity="0.7"
        />
        {/* Océanie / Indonésie */}
        <path
          d="M 660 240 Q 700 230 720 260 L 710 290 Q 680 300 660 280 Q 650 260 660 240 Z"
          fill="url(#landGradient)"
          stroke="#92400e"
          strokeWidth="0.5"
          opacity="0.7"
        />

        {/* Méridiens et parallèles décoratifs */}
        <g stroke="#92400e" strokeWidth="0.3" opacity="0.15" fill="none">
          <line x1="0" y1="100" x2="800" y2="100" />
          <line x1="0" y1="200" x2="800" y2="200" />
          <line x1="0" y1="300" x2="800" y2="300" />
          <line x1="200" y1="0" x2="200" y2="400" />
          <line x1="400" y1="0" x2="400" y2="400" />
          <line x1="600" y1="0" x2="600" y2="400" />
        </g>

        {/* Route Paris → origine — courbe de Bézier */}
        <path
          d={`M ${parisXY.x} ${parisXY.y} Q ${(parisXY.x + originXY.x) / 2} ${Math.min(parisXY.y, originXY.y) - 50} ${originXY.x} ${originXY.y}`}
          fill="none"
          stroke="url(#routeGradient)"
          strokeWidth="1.8"
          strokeDasharray="4 3"
          filter="url(#glow)"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-14"
            dur="3s"
            repeatCount="indefinite"
          />
        </path>

        {/* Marqueur Paris */}
        <g>
          <circle cx={parisXY.x} cy={parisXY.y} r="6" fill="#1c1917" opacity="0.3" />
          <circle cx={parisXY.x} cy={parisXY.y} r="4" fill="#92400e" />
          <circle cx={parisXY.x} cy={parisXY.y} r="2" fill="#fbbf24" />
          <text
            x={parisXY.x + 8}
            y={parisXY.y + 3}
            fontSize="11"
            fill="#1c1917"
            fontWeight="600"
            fontFamily="serif"
          >
            Paris
          </text>
          <text
            x={parisXY.x + 8}
            y={parisXY.y + 14}
            fontSize="8"
            fill="#92400e"
            fontStyle="italic"
          >
            SADIYA STYLE CUIR
          </text>
        </g>

        {/* Marqueur origine — pulsant */}
        <g>
          <circle cx={originXY.x} cy={originXY.y} r="14" fill="#fbbf24" opacity="0.15">
            <animate
              attributeName="r"
              from="8"
              to="18"
              dur="2s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              from="0.4"
              to="0"
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
          <circle cx={originXY.x} cy={originXY.y} r="6" fill="#1c1917" opacity="0.3" />
          <circle cx={originXY.x} cy={originXY.y} r="4" fill="#d97706" />
          <circle cx={originXY.x} cy={originXY.y} r="2" fill="#fef3c7" />
          <text
            x={originXY.x + 8}
            y={originXY.y - 5}
            fontSize="11"
            fill="#1c1917"
            fontWeight="700"
            fontFamily="serif"
          >
            {city}
          </text>
          <text
            x={originXY.x + 8}
            y={originXY.y + 7}
            fontSize="9"
            fill="#92400e"
            fontStyle="italic"
          >
            {country}
          </text>
        </g>

        {/* Étoile décorative — signature Eva Diaw */}
        <g transform={`translate(${originXY.x + 30}, ${originXY.y - 15})`} opacity="0.6">
          <path
            d="M 0 0 L 2 4 L 6 4 L 3 7 L 4 11 L 0 9 L -4 11 L -3 7 L -6 4 L -2 4 Z"
            fill="#fbbf24"
          />
        </g>
      </svg>

      {/* Légende */}
      <div className="px-4 py-2 border-t border-gold/20 bg-card/80 flex items-center justify-between text-[10px] text-foreground/60">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-gold" />
            Origine : {city}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-cuir" />
            Maison : Paris 10e
          </span>
        </div>
        <span className="italic">Rayon {rayonName}</span>
      </div>
    </div>
  )
}
